/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars, react/no-unescaped-entities */
import type { CindroSessionUser } from '@cindro/contracts';
import React, { useRef, useState } from 'react';

export function AcceptInvitePage({
  token,
  session,
  onAccepted,
  onSignIn,
  onSignUp,
  acceptFn
}: {
  token: string;
  session: { user: CindroSessionUser } | null;
  onAccepted: (_organizationId: string) => void;
  onSignIn: () => void;
  onSignUp: () => void;
  acceptFn: (_token: string) => Promise<{ organizationId: string; role: string }>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (redirectTimerRef.current !== null) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  async function handleAccept() {
    if (!token) {
      setError('Invalid invite link — the token is missing.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await acceptFn(token);
      setAccepted(true);
      redirectTimerRef.current = setTimeout(() => onAccepted(result.organizationId), 1500);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to accept invite.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="authShell">
      <div className="authCard" style={{ gap: 16 }}>
        <div className="authKicker">Cindro Admin</div>
        <h1>You&apos;ve been invited</h1>
        {accepted ? (
          <p style={{ color: 'var(--green)' }}>Invite accepted! Redirecting to your workspace…</p>
        ) : session ? (
          <>
            <p>
              You&apos;re signed in as <strong>{session.user.email}</strong>. Accept this invite to join the organization.
            </p>
            {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
            <button
              type="button"
              className="primaryButton"
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={busy || !token}
              onClick={handleAccept}
            >
              {busy ? 'Accepting…' : 'Accept invite'}
            </button>
          </>
        ) : (
          <>
            <p>Sign in or create an account to accept this invite and join the organization.</p>
            {!token && <p style={{ color: 'var(--red)', fontSize: 13 }}>Invalid invite link — token missing.</p>}
            <button
              type="button"
              className="primaryButton"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={onSignIn}
            >
              Sign in
            </button>
            <button
              type="button"
              className="secondaryButton"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={onSignUp}
            >
              Create account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
